import React, { ReactElement, memo } from "react";
import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import { InvitationsInvitationFragment } from "@/generated/graphql";

/**
 * Props for the ReceivedInvitationsView component.
 */
interface Props {
  invitations: InvitationsInvitationFragment[];
  renderItem: ListRenderItem<InvitationsInvitationFragment>;
  refreshing: boolean;
  widthContent: number;
  onLoadMore: () => void;
  onRefresh: () => void;
  renderListFooterComponent: () => ReactElement | null;
}
const keyExtractor = (item: InvitationsInvitationFragment) => item.id;

/**
 * View component for displaying the received invitations list with pagination and pull-to-refresh.
 * @param root0 - The component props
 * @param root0.invitations - Array of received invitation data fragments to display in the list
 * @param root0.renderItem - Function to render each individual received invitation item in the list
 * @param root0.refreshing - Boolean indicating if pull-to-refresh operation is in progress
 * @param root0.widthContent - The content width for responsive layout sizing
 * @param root0.onLoadMore - Callback function to load more invitations when scrolling reaches the end
 * @param root0.onRefresh - Callback function to refresh the received invitations list
 * @param root0.renderListFooterComponent - Function to render custom footer component for pagination or loading states
 */
const ReceivedInvitationsView = ({
  invitations,
  renderItem,
  refreshing,
  widthContent,
  onLoadMore,
  onRefresh,
  renderListFooterComponent,
}: Props) => (
  <FlashList
    className="self-center"
    contentContainerClassName="flex-grow p-3"
    data={invitations}
    scrollEventThrottle={16}
    // eslint-disable-next-line no-restricted-syntax -- runtime measured window width (widthContent); no closed sizing token, and FlashList is a bare re-export atom with no UNSAFE_style prop so width rides the pass-through style
    style={{ width: widthContent }}
    refreshing={refreshing}
    renderItem={renderItem}
    keyExtractor={keyExtractor}
    ListFooterComponent={renderListFooterComponent}
    onEndReached={onLoadMore}
    onRefresh={onRefresh}
    refreshControl={
      <RefreshControl
        refreshing={refreshing}
        onRefresh={onRefresh}
        colors={["white"]}
        tintColor="white"
      />
    }
    ItemSeparatorComponent={ItemSeparator}
  />
);

ReceivedInvitationsView.displayName = "ReceivedInvitationsView";

export default memo(ReceivedInvitationsView);
